import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Subscription } from 'rxjs';
import { CustomerService } from '../../services/customer.service';
import { ErrorService } from '../../services/error.service';
import { EvidenceScenarioService } from '../../services/evidence-scenario.service';
import { FileService } from '../../services/file.service';
import { GlobalService } from '../../services/global.service';
import { ModalComponent } from '../modal/modal.component';
import { filter, finalize, switchMap } from 'rxjs/operators';
import { SpinnerService } from '../../services/spinner.service';
import { UserService } from '../../services/user.service';
import {
	buildEvidenceScenarioLabelMap,
	EvidenceScenario,
	EvidenceScenarioLabelMap,
	resolveEvidenceScenarioLabel
} from '../../models/evidence-scenario';
import { FEATURE_VPOD_TYPE, isFeatureFlagEnabled } from '../../models/feature-flags';

@Component({
	selector: 'app-attachment-list',
	templateUrl: './attachment-list.component.html',
	styleUrls: ['./attachment-list.component.scss']
})
export class AttachmentListComponent implements OnChanges, OnDestroy, OnInit {
	@Input() public access: number;
	public fileExtensionRegex = /(?:\.([^.]+))?$/;
	@Input() public podAttachments: any[];
	@Input() public regularAttachments: any[];
	@Output() public remove: EventEmitter<any> = new EventEmitter<any>();
	@Input() public workOrder: any;
	@Input() public user: any;
	/** When true, PHOTO-category files are omitted from the regular list (they render in the RDI section instead). */
	@Input() public excludeRdiPhotos = false;
	public isFileOpening : boolean = false;

	/** code -> tileLabel for the active catalogue. Only read once _catalogueLoaded is true. */
	private _evidenceScenarioLabels: EvidenceScenarioLabelMap = new Map<string, string>();
	/**
	 * Only a successfully loaded catalogue displays anything. A failure leaves this false, so a client
	 * that cannot read the catalogue shows nothing rather than a screaming-snake code to a customer.
	 */
	private _catalogueLoaded = false;
	private _subscriptions = new Subscription();

	constructor(private _dialog: MatDialog,
				private _fileService: FileService,
				private _errorService: ErrorService,
				private _customerService: CustomerService,
				private _spinnerService: SpinnerService,
				private _globalService: GlobalService,
				private _userService: UserService,
				private _evidenceScenarioService: EvidenceScenarioService) {
	}

	ngOnInit(): void {
		this._openEvidenceScenarioGate();
	}

	ngOnChanges(): void {
		// Hosts pass already-split row arrays in, so this - not processAttachments() - is where host
		// data arrives. Re-labels with whatever the gate currently allows.
		this._applyEvidenceScenarioLabels();
	}

	ngOnDestroy(): void {
		this._subscriptions.unsubscribe();
	}

	/**
	 * Load the VPOD Type catalogue, once, and only when both gates pass:
	 *
	 *  1. A session token. A viewer with no session cannot read the catalogue at all, so skipping here
	 *     means that surface issues no request. Read from UserService rather than the `user` @Input,
	 *     which only one of the four hosts binds.
	 *  2. The per-tenant flag. Absent or off issues no catalogue request.
	 *
	 * A failure logs and leaves the gate closed - nothing renders. Only a loaded catalogue displays,
	 * which is what keeps an unresolvable client from showing raw codes to a customer.
	 */
	private _openEvidenceScenarioGate(): void {
		const currentUser = this._userService.getUser();
		if (!currentUser || !currentUser.token) {
			return;
		}
		this._subscriptions.add(
			this._globalService.getFeatureFlags()
				.pipe(
					filter((featureFlags: any) => isFeatureFlagEnabled(featureFlags && featureFlags[FEATURE_VPOD_TYPE])),
					switchMap(() => this._evidenceScenarioService.getActive())
				)
				.subscribe({
					next: (scenarios: EvidenceScenario[]) => {
						this._evidenceScenarioLabels = buildEvidenceScenarioLabelMap(scenarios);
						this._catalogueLoaded = true;
						// The catalogue resolves after the rows arrive, so re-label.
						this._applyEvidenceScenarioLabels();
					},
					// Logged only - the omitted third argument is what makes this log-without-UI. A file
					// list must stay usable through a catalogue outage.
					error: (error) => this._errorService.error(error, 'getActiveEvidenceScenarios Failed')
				})
		);
	}

	/**
	 * Precompute each row's label so the template reads a field instead of calling a function. Every
	 * row is written on every pass, including with '', so a closed gate leaves no stale value behind.
	 */
	private _applyEvidenceScenarioLabels(): void {
		this._labelRows(this.regularAttachments);
		this._labelRows(this.podAttachments);
	}

	private _labelRows(attachments: any[]): void {
		if (!attachments) {
			return;
		}
		for (const attachment of attachments) {
			if (attachment) {
				attachment._evidenceScenarioLabel = this._catalogueLoaded
					? resolveEvidenceScenarioLabel(attachment.evidenceScenario, this._evidenceScenarioLabels)
					: '';
			}
		}
	}

	openFile(file): void {
		if (this.isFileOpening) {
        	return;
    	}

		this.isFileOpening = true;
		this._spinnerService.startSpinner();
		const fileType = this.fileExtensionRegex.exec(file.fileName)[1];
		const selectedCustomer = this._customerService.getSelectedCustomer();
		const convertTo = fileType === 'pdf' ? fileType : selectedCustomer ? selectedCustomer.defaultImageConversion : 'NO_CONVERSION';
		requestAnimationFrame(() => {
			this._fileService.getFile(file.id, convertTo)
				.pipe(finalize(() => {
					this.isFileOpening = false;
					this._spinnerService.stopSpinner();
				}))
				.subscribe(
				(response) => {
					// Success
					// We get base64 but need a blob so convert it, using mime-types library to
					// guess the mime type based on file extension - This is not fool proof as file
					// extensions can be spoofed
					const blob = this._fileService.convertToBlob(response);
					// If IE or Edge
					if ((window.navigator as any).msSaveBlob) {
						return (window.navigator as any).msSaveBlob(blob, response.fileName);
					}
					const reader: any = new FileReader();
					reader.onload = (e) => {
						let content;
						if (reader.result.includes('image')) {
							content = new Image();
							content.src = reader.result;
						}
						const newTab = window.open();
						if (newTab) {
							newTab.document.write(content ? content.outerHTML : '<iframe src="' + reader.result + '" frameborder="0" class="no-border-full-screen" allowfullscreen></iframe>');
						} else {
							this._errorService.error({
								detailedMsg: 'Pop-up Blocker is enabled! Please add this site to your exception list.',
								generalMsg: 'Pop-up blocked!'
							}, null, 'modal');
						}
					};
					reader.readAsDataURL(blob);
				},
				(error) => {
					this._errorService.error(error, 'openFile Failed', 'modal');
				}
			);
		});
	}

	processAttachments(): void {
		this.regularAttachments = this.workOrder.attachments.filter((a) => a.fileCategory !== 'POD_IMAGE' && !(this.excludeRdiPhotos && a.fileCategory === 'PHOTO'));
		this.podAttachments = this.workOrder.attachments.filter((a) => a.fileCategory === 'POD_IMAGE');
		// An internal re-split does not go through ngOnChanges, so re-label here too.
		this._applyEvidenceScenarioLabels();
	}

	removeAttachment(file): void {
		if (!this.workOrder) {
			this.remove.emit(file);
		} else {
			const dialogRef = this._dialog.open(ModalComponent, {
				data: {
					title: 'Warning',
					message: 'You are about to delete an attachment.  Would you like to proceed?',
					showCancel: true,
					cancelText: 'No',
					okText: 'Yes'
				}
			});
			dialogRef.afterClosed().subscribe((result) => {
				if (result) {
					this._fileService.deleteFile(file.id).subscribe(() => {
						const idx = this.workOrder.attachments.indexOf(file);
						if (idx !== -1) {
							this.workOrder.attachments.splice(idx, 1);
							this.processAttachments();
						}
					}, error => {
						this._errorService.error(error, 'deleteFile Failed', 'modal');
					});
				} else {
					return false;
				}
			});
		}
	}
}
